import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import CreatableSelect from 'react-select/creatable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '@fortawesome/free-regular-svg-icons';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import { generateClient } from '@aws-amplify/api';
import { getSubjectContactsWithSummary as getSubjectContacts, getSubjects } from '@/graphql/small-queries';
import { getDistinctSubjectStatuses } from '@/graphql/queries';
import { upsertSubject } from "@/graphql/small-mutations";
import TranscriptModal from '@/TranscriptModal';
import CallModal from './CallModal';
import ContactEntry from '@/ContactEntry';
import Modal from '@/Modal';
import { outcome_color } from '@/utils';
import useSubscription from '@/hooks/useSubscription';

const client = generateClient();

const recordingIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faVolumeLow} size="lg" />);
const transcriptIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faQuoteLeft} size="lg" />);
const checkIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faCircleCheck} size="lg" />);
const uncheckIcon = (<FontAwesomeIcon className="fg-purple" icon={icons.faCircle} size="lg" />);
const phoneIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faPhone} size="lg" />);
const emailIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faEnvelope} size="lg" />);
const textIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faSms} size="lg" />);
const agentIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faUser} size="lg" />);

export default ({ catchError, data: _data, path = '/app/pipeline/subjects/', setModal, subject_id }) => {
	const isConstructed = useRef(false);
	const [isLoading, setIsLoading] = useState(true);
	const [busy, setBusy] = useState({});
	const updateBusy = useCallback((id, value) => {
		setBusy(prevState => ({ ...prevState, [id]: value }));
	}, []);
	const [data, setData] = useState();
	const [profile, setProfile] = useState();
	const [workflows, setWorkflows] = useState();
	const [contacts, setContacts] = useState();

	const [statuses, _setStatuses] = useState();
	const setStatuses = useCallback(async new_statuses => {
		_setStatuses(old_statuses => {
			// I want these always to be sorted
			const _new_statuses = _.orderBy(new_statuses, 'status');
			const deletes = (old_statuses || []).filter(e => !_new_statuses.find(f => f.id === e.id)).map(e => ({ id: e.id, key: 'subject_statuses' }));
			// Call upsertSubject with subject_statuses
			const input = {
				subjects: { id: subject_id },
				// Currate this into { id (optional), status }
				subject_statuses: _new_statuses.map(e => ({ ...(e.id ? { id: e.id } : undefined), status: e.status }))
			};
			const input_delete = deletes.length > 0 ? deletes : undefined;
			client.graphql({ query: upsertSubject, variables: { input, input_delete } })
				.then(response => {
					console.log(response);
				})
				.catch(error => {
					console.error(error);
				});
			return _new_statuses;
		});
	}, []);
	const addStatus = useCallback(new_status => {
		setStatuses([...(statuses ? statuses : []), new_status]);
	}, [statuses]);

	const filters_ref = useRef({ sorting: { field: 'started_at', order: 'desc' } });
	useSubscription({ filters_ref, rankings_key: 'subject_contacts', setRows: setContacts });

	useEffect(() => {
		if (isConstructed.current) {
			setProfile(_data || {});
			_setStatuses(_data?.subject_statuses);
			const _workflows = _.cloneDeep(_data?.subject_workflows || []);
			_workflows.forEach(w => {
				try { w.tasks_json = JSON.parse(w.tasks_json || '{}'); } catch (_) {};
				try { w.workflow_tasks_json = JSON.parse(w.workflow_tasks_json || '[]'); } catch (_) {};
			});
			setWorkflows(_workflows);
		}
	}, [_data]);

	const [distinctStatuses, setDistinctStatuses] = useState();
	const loadDistinctStatuses = useCallback(() => {
	return client.graphql({ query: getDistinctSubjectStatuses })
		.then(res => setDistinctStatuses(res.data.getDistinctSubjectStatuses.statuses.map(status => ({ status }))));
	}, []);

	const profile_el = useMemo(() => {
		if (!profile) return busy.profile ? (
			<div className="text-center" style={{marginBottom:'2rem',marginTop:'2rem'}}>
				<button className="btn btn-primary" type="button" disabled>
					<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
					Loading...
				</button>
			</div>
		) : undefined;
		const email_address = profile.subject_emails?.[0]?.email_address;
		const voice_numbers = _.orderBy(profile.subject_phone_numbers || [], 'created_at')?.slice(0, 1);
		const text_numbers = profile.sms_opt_in ? _.orderBy(profile.subject_phone_numbers || [], 'created_at')?.slice(0, 1) : [];
		const onCall = ({ channel }) => () => {
			setModal(<CallModal catchError={catchError} channel={channel} data={profile} onClose={() => setModal()} />);
		};
		const onAgent = () => {
			setModal(<Modal onClose={() => setModal()} size="md" title="Transfer Live Call"><ContactEntry data={{ ...profile, contact_id: '123' }} /></Modal>);
		};
		const profile_el = (
			<>
				<Link className="text-decoration-none fg-purple" to={{ pathname: path + subject_id, search: location.search }}><h4 className="mb-3">{profile.full_name_last}</h4></Link>
				{voice_numbers.map((e, i) => (<div className="row g-0 mb-2 cursor-pointer" key={i} onClick={onCall({ channel: 'study_config_voice' })}><div className="col">{e.phone_number}</div><div className="col-auto">{phoneIcon}</div></div>))}
				{text_numbers.map((e, i) => (<div className="row g-0 mb-2 cursor-pointer" key={i} onClick={onCall({ channel: 'study_config_sms' })}><div className="col">{e.phone_number}</div><div className="col-auto">{textIcon}</div></div>))}
				{false && email_address && <div className="row g-0 mb-2"><div className="col">{email_address}</div><div className="col-auto"><a href={`mailto: ${email_address}`}>{emailIcon}</a></div></div>}
				{['demo', 'dev', undefined].indexOf(process.env.USER_BRANCH) !== -1 && (
					<div className="row g-0 cursor-pointer" onClick={onAgent}>
						<div className="col">Transfer Live Call</div>
						<div className="col-auto">{agentIcon}</div>
					</div>
				)}
				<h5 className="text-center bg-purple text-white p-1 mt-4 mb-3">Status</h5>
				<CreatableSelect
					className="form-control-soft"
					getOptionLabel={e => e.status ?? e.label }
					getOptionValue={e => e.status ?? e.value }
					isMulti
					name="statuses"
					onChange={setStatuses}
					onCreateOption={option => addStatus({ status: option })}
      				onMenuOpen={() => loadDistinctStatuses()}
					options={distinctStatuses}
					placeholder="Statuses"
					styles={{
					menu: base => ({
						...base,
						zIndex: 5
					}),
					menuList: base => ({
						...base,
						maxHeight: 250
					}),
					multiValueLabel: (base) => ({
						...base,
						whiteSpace: 'normal'
					})
					}}
					value={statuses}
				/>
			</>
		);
		return profile_el;
	}, [JSON.stringify(busy), distinctStatuses, profile, statuses]);

	const workflows_el = useMemo(() => {
		if (!workflows) return busy.workflows ? (
			<div className="text-center" style={{marginBottom:'2rem',marginTop:'2rem'}}>
				<button className="btn btn-primary" type="button" disabled>
					<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
					Loading...
				</button>
			</div>
		) : undefined;
		const workflows_el = workflows.map((w, w_i) => {
			let tasks_el;
			const setTask = ({ task_id, status, workflow_id }) => {
				setWorkflows(prevState => {
					const newWorkflows = _.cloneDeep(prevState);
					const _w = newWorkflows.find(e => e.id === workflow_id);
					if (_w) {
						if (!_w.tasks_json) {
							// Initialize tasks_json. Will remove later if empty.
							_w.tasks_json = {};
						}
						const _t = _w.tasks_json[task_id] || {};
						if (status) {
							_t.outcome = 'Done';
						} else {
							// No need to store null
							delete _t.outcome;
						}
						if (Object.keys(_t).length > 0) {
							_w.tasks_json[task_id] = _t;
						} else {
							// Remove empty key
							delete _w.tasks_json[task_id];
						}
						if (Object.keys(_w.tasks_json).length === 0) {
							_w.tasks_json = null;
						}
						client.graphql({
							query: upsertSubject,
							variables: {
								input: {
									subjects: { id: subject_id },
									subject_workflows: {
										id: workflow_id,
										tasks_json: _w.tasks_json === null ? null : JSON.stringify(_w.tasks_json)
									}
								},
								input_integrations: { clinicalConductor: false }
							}
						});
					}
					return newWorkflows;
				});
			};
			tasks_el = w.workflow_tasks_json.map((t, t_i) => {
				const _t = w.tasks_json?.[t.id] || {};
				const completed_at = moment.utc(t.completed_at).format('M/DD/YYYY h:mm A');
				return (
					<div className="mb-3" key={t_i}>
						<div className="row g-0 cursor-pointer" onClick={() => setTask({ task_id: t.id, status: _t.outcome !== 'Done', workflow_id: w.id })}>
							<div className="col-auto pe-2">
								{_t.outcome === 'Done' ? (
									checkIcon
								) : (
									uncheckIcon
								)}
							</div>
							<div className="col">{t.purpose}</div>
						</div>
						{/*}
						<div className="row small">
							<div className="col offset-2">{completed_at} Chris Faherty</div>
						</div>
						*/}
					</div>
				);
			});
			return (
				<React.Fragment key={w_i}>
					<h5 className="text-center bg-purple text-white p-1 mb-3">{w.workflow_name}</h5>
					{tasks_el}
				</React.Fragment>
			)
		});
		return workflows_el;
	}, [JSON.stringify(busy), workflows]);

	const contacts_el = useMemo(() => {
		if (!contacts) return busy.contacts ? (
			<div className="text-center" style={{marginBottom:'2rem',marginTop:'2rem'}}>
				<button className="btn btn-primary" type="button" disabled>
					<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
					Loading...
				</button>
			</div>
		) : undefined;
		const contacts_el = contacts.map((e, i) => {
			const started_at = moment(e.started_at).format('M/DD/YYYY h:mm A');
			const onClose = () => {
				setModal();
			};
			const onClick = ({ autoplay = false } = {}) => setModal(<TranscriptModal autoplay={autoplay} id={e.contact_id} onClose={onClose} />);
			let d;
			if (e.ended_at) {
				const _d = moment.duration(moment(e.ended_at).diff(e.started_at));
				const m = _d.minutes();
				const s = _d.seconds();
				d = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
			}
			return (
				<div className="row mb-4 g-0" key={i}>
					<div className="row mb-1 g-0">
						<div className="col"><b>{started_at}</b>&nbsp;{d}</div>
						{e.recording_url && <div className="col-auto ms-2 btn-link cursor-pointer" onClick={() => onClick({ autoplay: true })}>{recordingIcon}</div>}
						<div className="col-auto ms-2 btn-link cursor-pointer" onClick={onClick}>{transcriptIcon}</div>
					</div>

					{e.summary && (
						<div className="border rounded-3 p-2 bg-light-purple text-break">
							<Markdown
								children={e.summary}
								remarkPlugins={[remarkGfm, remarkBreaks]}
								components={{
								ul: ({ node, ...props }) => (
									<ul style={{ margin: 0, paddingLeft: '1.25rem' }} {...props} />
								),
								h1: 'h3',
								h2: 'h4',
								h3: 'h4',
								a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
								code: ({ inline, children }) =>
									!inline ? (
									<pre style={{ background: "#f6f8fa", padding: "0.75rem", borderRadius: "6px" }}>
										<code>{children}</code>
									</pre>
									) : (
									<code style={{ background: "#eee", padding: "0.2rem 0.4rem", borderRadius: "4px" }}>{children}</code>
									)
								}}
							/>
						</div>
					)}
					{(e.outcomes || []).map((outcome, i) => <span className={`col-auto mt-2 me-2 p-2 badge ${outcome_color(outcome)}`} key={i}>{outcome}</span>)}
				</div>
			)
		});
		return contacts_el;
	}, [JSON.stringify(busy), contacts]);

	if (!isConstructed.current) {
		isConstructed.current = true;
		updateBusy('profile', !_data);
		(_data ? Promise.resolve({ data: { getSubjects: { subjects: [_data] } } }) : client.graphql({ query: getSubjects, variables: { id: subject_id } }))
			.then(response => {
				setProfile(response.data.getSubjects.subjects?.[0] || {});
				const _workflows = _.cloneDeep(response.data.getSubjects.subjects?.[0]?.subject_workflows || []);
				_workflows.forEach(w => {
					try { w.tasks_json = JSON.parse(w.tasks_json || '{}'); } catch (_) {};
					try { w.workflow_tasks_json = JSON.parse(w.workflow_tasks_json || '[]'); } catch (_) {};
				});
				setWorkflows(_workflows);
				updateBusy('profile', false);
				updateBusy('contacts', true);
				client.graphql({ query: getSubjectContacts, variables: { sorting: { field: 'started_at', order: 'desc' }, subject_id } })
					.then(response => {
						setContacts(response.data.getSubjectContacts.subject_contacts || []);
						updateBusy('contacts', false);
					});
			});
	}

	return (
		<div className="pe-2">
			{profile_el && (
				<div className="mb-4">
					{profile_el}
				</div>
			)}
			{workflows_el && (
				<div className="mb-4">
					{workflows_el}
				</div>
			)}
			{contacts_el && (
				<div className="mb-4">
					<h5 className="text-center bg-purple text-white p-1 mb-3">Engagements</h5>
					{contacts_el}
				</div>
			)}
		</div>
	);
};
